import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Moon,
  Sun,
  Menu,
  X,
  User,
  LogOut,
  Plus,
  Globe,
  Home,
  Sparkles,
  HelpCircle,
  BarChart3,
  FileText,
  LayoutDashboard,
  ChevronDown,
  Settings,
  Zap
} from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import LanguageToggle from '@/components/ui/LanguageToggle';
import ThemeToggle from '@/components/ui/ThemeToggle';

import { useUserAvatar } from '@/hooks/useUserAvatar';
import { useAvatarRefresh, useProfileRefresh } from '@/contexts/AvatarContext';

const Navbar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [activeSection, setActiveSection] = useState<string>('');
  const { refreshTrigger } = useAvatarRefresh();
  const { profileRefreshTrigger } = useProfileRefresh();
  const userAvatar = useUserAvatar(currentUser, refreshTrigger);



  // Utility function to scroll to section with retry logic
  const scrollToSection = useCallback((sectionId: string, retries = 0) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else if (retries < 10) {
      scrollTimerRef.current = setTimeout(() => scrollToSection(sectionId, retries + 1), 100);
    }
  }, []);

  // Handle anchor scrolling after page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && pathname === '/') {
      const sectionId = hash.substring(1);
      scrollTimerRef.current = setTimeout(() => scrollToSection(sectionId), 300);
    }
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [pathname, scrollToSection]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      if (pathname === '/' && !currentUser) {
        const sections = ['hero-section', 'features-section', 'how-it-works-section'];
        const scrollPosition = window.scrollY + 100;

        let currentSection = '';

        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              currentSection = sectionId;
              break;
            }
          }
        }

        if (!currentSection) {
          currentSection = 'hero-section';
        }

        setActiveSection(currentSection);
      } else {
        setActiveSection('');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pathname, currentUser]);

  // Load and update display name
  useEffect(() => {
    const loadDisplayName = async () => {
      if (!currentUser) {
        setDisplayName('');
        return;
      }

      try {
        if (currentUser.isAnonymous) {
          // For anonymous users, get from localStorage
          const { getAnonymousUserProfile } = await import('@/services/profileService');
          const profile = getAnonymousUserProfile(currentUser.uid);
          setDisplayName(profile?.displayName || '');
        } else {
          // For authenticated users, get from Firebase Auth first, then Firestore if needed
          if (currentUser.displayName) {
            setDisplayName(currentUser.displayName);
          } else {
            const { getUserProfile } = await import('@/services/profileService');
            const profile = await getUserProfile(currentUser.uid);
            setDisplayName(profile?.displayName || currentUser.email || '');
          }
        }
      } catch (error) {
        console.error('Error loading display name:', error);
        setDisplayName(currentUser.displayName || currentUser.email || '');
      }
    };

    loadDisplayName();
  }, [currentUser, profileRefreshTrigger]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Different navigation links for logged-in vs logged-out users
  const publicNavLinks = [
    { to: '/#hero-section', label: t('nav.home'), icon: Home },
    { to: '/#features-section', label: t('nav.features'), icon: Sparkles },
    { to: '/#how-it-works-section', label: t('nav.howItWorks'), icon: Zap },
    { to: '/help', label: t('nav.help'), icon: HelpCircle },
  ];

  const authenticatedNavLinks = [
    { to: '/create', label: t('nav.createPoll'), icon: Plus },
    { to: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { to: '/polls', label: t('nav.polls'), icon: FileText },
    { to: '/analytics', label: t('nav.analytics'), icon: BarChart3 },
    { to: '/help', label: t('nav.help'), icon: HelpCircle },
  ];

  const navLinks = currentUser ? authenticatedNavLinks : publicNavLinks;

  const isActive = (path: string) => {
    // Handle regular page paths
    if (!path.includes('#')) {
      return pathname === path;
    }

    // Handle anchor links - check if we're on home page and the section is active
    if (pathname === '/' && !currentUser) {
      const sectionId = path.split('#')[1];
      return activeSection === sectionId;
    }

    return false;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'navbar-blur shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-x-2 group"
          >
            <m.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <img
                src="/logo_256.png"
                alt="Meet Logo"
                className="size-8"
              />
            </m.div>
            <img
              src="/MEET.png"
              alt="Meet"
              className="h-6"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-x-8">
            {navLinks.map((link) => {
              const isCreatePoll = link.to === '/create';
              const isDashboard = link.to === '/dashboard';
              const isAnchorLink = link.to.includes('#');

              const handleNavLinkClick = (e: React.MouseEvent) => {
                if (isAnchorLink) {
                  e.preventDefault();
                  const sectionId = link.to.split('#')[1];

                  // If we're not on the home page, navigate there first
                  if (pathname !== '/') {
                    navigate(`/#${sectionId}`);
                    return;
                  }

                  // If we're already on home page, scroll to section
                  scrollToSection(sectionId);
                }
              };

              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={handleNavLinkClick}
                  className={`relative flex items-center gap-x-2 px-3 py-2 text-sm font-medium transition-all duration-200 ${
                    // Special styling for Create Poll button when authenticated
                    currentUser && isCreatePoll
                      ? 'bg-primary-600 text-white hover:bg-primary-700 rounded-lg'
                      : // Special styling for Dashboard button when authenticated
                      currentUser && isDashboard
                      ? 'border border-zinc-300 dark:border-zinc-600 rounded-lg text-zinc-700 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-300 dark:hover:border-primary-600'
                      : // Default styling
                      isActive(link.to)
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-zinc-700 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  <m.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <link.icon className="size-4" />
                  </m.div>
                  <span>{link.label}</span>
                  {isActive(link.to) && !currentUser && (
                    <m.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-x-4">
            {/* Create Poll Button - Only for non-authenticated users */}
            {!currentUser && (
              <Link
                to="/create"
                className="hidden sm:flex items-center gap-x-2 btn-primary text-sm px-4 py-2"
              >
                <Plus className="size-4" />
                <span>{t('nav.createPoll')}</span>
              </Link>
            )}

            {/* Language and Theme Toggles - Only show for non-authenticated users */}
            {!currentUser && (
              <>
                {/* Language Toggle */}
                <m.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleLanguage}
                  className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors duration-200"
                  title={`Switch to ${language === 'en' ? 'Español' : 'English'}`}
                >
                  <div className="flex items-center gap-x-1">
                    <Globe className="size-4" />
                    <span className="text-xs font-medium">
                      {language.toUpperCase()}
                    </span>
                  </div>
                </m.button>

                {/* Theme Toggle */}
                <m.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleTheme}
                  className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors duration-200"
                  title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <m.div
                      key={theme}
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 20, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {theme === 'light' ? (
                        <Moon className="size-5" />
                      ) : (
                        <Sun className="size-5" />
                      )}
                    </m.div>
                  </AnimatePresence>
                </m.button>
              </>
            )}

            {/* Auth Buttons */}
            {currentUser ? (
              <div className="hidden md:flex items-center gap-x-4">
                {/* User Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className="flex items-center gap-x-2 text-zinc-700 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 p-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  >
                    {!userAvatar.isLoading && userAvatar.url ? (
                      <div className="size-6 rounded-full overflow-hidden">
                        <img
                          src={userAvatar.url}
                          alt="User avatar"
                          className="size-full object-cover"
                          onError={(e) => {
                            // Fallback to User icon if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'inline';
                          }}
                        />
                        <User className="size-6" style={{ display: 'none' }} />
                      </div>
                    ) : (
                      <User className="size-6" />
                    )}
                    <span className="text-sm font-medium max-w-[70px] truncate">
                      {displayName?.substring(0, 7) || currentUser.email?.substring(0, 7) || t('common.user')}
                    </span>
                    <ChevronDown className="size-4" />
                  </button>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {isUserDropdownOpen && (
                      <m.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-48 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 py-1 z-50"
                        onMouseLeave={() => setIsUserDropdownOpen(false)}
                      >
                        <Link
                          to="/profile"
                          onClick={() => setIsUserDropdownOpen(false)}
                          className="flex items-center gap-x-2 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors duration-200"
                        >
                          <User className="size-4" />
                          <span>{t('nav.profile')}</span>
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setIsUserDropdownOpen(false)}
                          className="flex items-center gap-x-2 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors duration-200"
                        >
                          <Settings className="size-4" />
                          <span>{t('nav.settings')}</span>
                        </Link>
                        <div className="flex items-center justify-between px-4 py-3 text-sm text-zinc-700 dark:text-zinc-300">
                          <div className="flex items-center gap-x-2">
                            <Globe className="size-4" />
                            <span>{t('nav.language')}</span>
                          </div>
                          <LanguageToggle
                            language={language}
                            onChange={(newLanguage) => {
                              if (newLanguage !== language) {
                                toggleLanguage();
                              }
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 text-sm text-zinc-700 dark:text-zinc-300">
                          <div className="flex items-center gap-x-2">
                            {theme === 'dark' ? (
                              <Sun className="size-4" />
                            ) : (
                              <Moon className="size-4" />
                            )}
                            <span>{t('nav.theme')}</span>
                          </div>
                          <ThemeToggle
                            theme={theme}
                            onChange={(newTheme) => {
                              if (newTheme !== theme) {
                                toggleTheme();
                              }
                            }}
                          />
                        </div>
                        <div className="border-t border-zinc-200 dark:border-zinc-700 my-1"></div>
                        <button
                          onClick={() => {
                            setIsUserDropdownOpen(false);
                            handleLogout();
                          }}
                          className="flex items-center gap-x-2 w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                        >
                          <LogOut className="size-4" />
                          <span>{t('nav.logout')}</span>
                        </button>
                      </m.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-x-4">
                <Link
                  to="/login"
                  className="text-zinc-700 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 text-sm font-medium"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/signup"
                  className="btn-primary text-sm px-4 py-2"
                >
                  {t('nav.signup')}
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <m.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-zinc-100 dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300"
            >
              <AnimatePresence mode="wait" initial={false}>
                <m.div
                  key={isMenuOpen ? 'close' : 'menu'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMenuOpen ? (
                    <X className="size-5" />
                  ) : (
                    <Menu className="size-5" />
                  )}
                </m.div>
              </AnimatePresence>
            </m.button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <m.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden border-t border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navLinks.map((link) => {
                  const isCreatePoll = link.to === '/create';
                  const isDashboard = link.to === '/dashboard';
                  const isAnchorLink = link.to.includes('#');

                  const handleNavLinkClick = (e: React.MouseEvent) => {
                    setIsMenuOpen(false);
                    if (isAnchorLink) {
                      e.preventDefault();
                      const sectionId = link.to.split('#')[1];

                      // If we're not on the home page, navigate there first
                      if (pathname !== '/') {
                        navigate(`/#${sectionId}`);
                        return;
                      }

                      // If we're already on home page, scroll to section
                      scrollToSection(sectionId);
                    }
                  };

                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={handleNavLinkClick}
                      className={`flex items-center gap-x-2 px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                        // Special styling for Create Poll button when authenticated
                        currentUser && isCreatePoll
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : // Special styling for Dashboard button when authenticated
                          currentUser && isDashboard
                          ? 'border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                          : // Default styling
                          isActive(link.to)
                          ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'text-zinc-700 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <link.icon className="size-4" />
                      <span>{link.label}</span>
                    </Link>
                  );
                })}

                {/* Create Poll Button - Only for non-authenticated users */}
                {!currentUser && (
                  <Link
                    to="/create"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-x-2 px-3 py-2 rounded-md text-base font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors duration-200"
                  >
                    <Plus className="size-4" />
                    <span>{t('nav.createPoll')}</span>
                  </Link>
                )}

                {currentUser ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-x-2 px-3 py-2 rounded-md text-base font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors duration-200"
                    >
                      {!userAvatar.isLoading && userAvatar.url ? (
                        <div className="size-6 rounded-full overflow-hidden">
                          <img
                            src={userAvatar.url}
                            alt="User avatar"
                            className="size-full object-cover"
                            onError={(e) => {
                              // Fallback to User icon if Google avatar fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'inline';
                            }}
                          />
                          <User className="size-6" style={{ display: 'none' }} />
                        </div>
                      ) : (
                        <User className="size-4" />
                      )}
                      <span>{t('nav.profile')}</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-x-2 w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                    >
                      <LogOut className="size-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-3 py-2 rounded-md text-base font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors duration-200"
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-3 py-2 rounded-md text-base font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors duration-200"
                    >
                      {t('nav.signup')}
                    </Link>
                  </>
                )}
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
